/**
 * How long ago THIS PAGE LOAD became authenticated, read at the moment an event is captured.
 *
 * Deliberately not the age of the Auth0 session: the clock starts when `TokenContextProvider` first
 * observes authentication, so a reload against a day-old session reports `<5m`. That is the right
 * measure for the question we ask of it — how long a tab sat before erroring — but it would be the
 * wrong one for "how stale is this session", which would need `auth_time` from the ID token.
 *
 * It lives outside `TokenContextProvider` so `sentry.config.ts` can stamp it in `beforeSend`. The
 * provider's Sentry effect only re-runs when auth state changes, so an age computed there freezes on
 * a quiet tab: an error three hours into an idle tab would report seconds. That idle time is exactly
 * what sc-142309 / DFH-CORP-SITES-JSG is about, so it has to be read at capture time.
 */
let lastAuthenticatedAtMs: number | undefined

export const markPageAuthenticated = (nowMs: number = Date.now()): void => {
  lastAuthenticatedAtMs = nowMs
}

/** Test seam: clears the page-load state this module accumulates. */
export const resetPageAuthenticatedAt = (): void => {
  lastAuthenticatedAtMs = undefined
}

/**
 * Bucketed so the age is groupable as a Sentry tag. Raw seconds would be unbounded tag
 * cardinality, which Sentry indexes and we would rather not pay for.
 */
export const pageAuthAgeBucketFrom = (ageMs: number): string => {
  const minutes = ageMs / 60000
  if (minutes < 5) {
    return '<5m'
  }
  if (minutes < 30) {
    return '5-30m'
  }
  if (minutes < 120) {
    return '30m-2h'
  }
  if (minutes < 480) {
    return '2h-8h'
  }
  return '>8h'
}

export const secondsSincePageAuthenticated = (nowMs: number = Date.now()): number | undefined =>
  lastAuthenticatedAtMs === undefined
    ? undefined
    : Math.round((nowMs - lastAuthenticatedAtMs) / 1000)

export const pageAuthAgeBucket = (nowMs: number = Date.now()): string | undefined =>
  lastAuthenticatedAtMs === undefined
    ? undefined
    : pageAuthAgeBucketFrom(nowMs - lastAuthenticatedAtMs)
